import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const [mode, setMode] = useState("login"); // login | forgot | reset | done
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetKey, setResetKey] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (!loading && user) return <Navigate to="/admin" replace />;

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const doForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      if (data.is_admin_email) toast.success("Admin email confirmed. Enter your recovery key.");
      else toast.info("If this email is registered, follow the steps below.");
      setMode("reset");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (newPassword !== confirm) { toast.error("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email, reset_key: resetKey, new_password: newPassword });
      setMode("done");
      toast.success("Password updated. You can sign in now.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  const InputCls = "mt-2 bg-[#0B1F3A] border-[#D4AF37]/25 text-[#F5E8D8] rounded-full";

  return (
    <div className="min-h-screen grid place-items-center bg-[#0B1F3A] text-[#F5E8D8] px-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <img src="https://images.unsplash.com/photo-1528722828814-77b9b83aafb2?w=1600" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0B1F3A]/80" />
      </div>

      <div className="relative z-10 w-full max-w-md rounded-3xl border border-[#D4AF37]/30 bg-[#0F284B] p-10 shadow-2xl">
        <div className="flex items-center gap-3 mb-8">
          <img src="/assets/logo-mono.png" alt="YourAstroHub Logo" className="h-12 w-auto" />
          <div>
            <div className="font-serif text-xl">YourAstroHub</div>
            <div className="text-[0.6rem] uppercase tracking-[0.3em] text-[#AD974F]">Admin Console</div>
          </div>
        </div>

        {mode === "login" && (
          <form onSubmit={doLogin} data-testid="admin-login-form">
            <h2 className="font-serif text-3xl mb-2">Welcome back.</h2>
            <p className="text-sm text-[#F5E8D8]/60 mb-8">Sign in to manage your content.</p>
            <div className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-[#F5E8D8]/80">Email</Label>
                <Input id="email" data-testid="admin-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className={InputCls} />
              </div>
              <div>
                <Label htmlFor="password" className="text-[#F5E8D8]/80">Password</Label>
                <Input id="password" data-testid="admin-password" type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} className={InputCls} />
              </div>
            </div>
            <Button type="submit" data-testid="admin-login-submit" disabled={busy} size="lg" className="w-full mt-8 rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold">
              {busy ? "Signing in..." : "Sign in"}
            </Button>
            <button type="button" data-testid="forgot-password-link" onClick={() => setMode("forgot")}
              className="mt-4 w-full text-center text-xs text-[#D4AF37] hover:underline flex items-center justify-center gap-1.5">
              <KeyRound className="w-3.5 h-3.5" />Forgot password?
            </button>
          </form>
        )}

        {mode === "forgot" && (
          <form onSubmit={doForgot} data-testid="forgot-form">
            <button type="button" onClick={() => setMode("login")} className="text-xs text-[#F5E8D8]/60 hover:text-[#D4AF37] mb-4 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" />Back to Sign in</button>
            <h2 className="font-serif text-3xl mb-2">Forgot password?</h2>
            <p className="text-sm text-[#F5E8D8]/60 mb-6">Enter your admin email to begin the recovery process.</p>
            <div>
              <Label className="text-[#F5E8D8]/80">Admin Email</Label>
              <Input data-testid="forgot-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className={InputCls} placeholder="admin@yourastrohub.com" />
            </div>
            <Button type="submit" data-testid="forgot-submit" disabled={busy} size="lg" className="w-full mt-6 rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold">
              {busy ? "Checking..." : "Continue"}
            </Button>
          </form>
        )}

        {mode === "reset" && (
          <form onSubmit={doReset} data-testid="reset-form">
            <button type="button" onClick={() => setMode("forgot")} className="text-xs text-[#F5E8D8]/60 hover:text-[#D4AF37] mb-4 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" />Back</button>
            <h2 className="font-serif text-3xl mb-2">Reset password</h2>
            <p className="text-sm text-[#F5E8D8]/60 mb-2">
              Enter the <b className="text-[#D4AF37]">Admin Recovery Key</b> to set a new password.
            </p>
            <div className="text-xs text-[#F5E8D8]/60 mb-6 p-3 rounded-xl bg-[#0B1F3A] border border-[#D4AF37]/20">
              <div className="font-semibold text-[#D4AF37] mb-1">Where is the key?</div>
              It is the value of <code>ADMIN_RESET_KEY</code> in your <code>backend/.env</code> (preview) or your Emergent production Environment Variables panel.
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-[#F5E8D8]/80">Admin Email</Label>
                <Input data-testid="reset-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className={InputCls} />
              </div>
              <div>
                <Label className="text-[#F5E8D8]/80">Admin Recovery Key</Label>
                <Input data-testid="reset-key" type="text" required value={resetKey} onChange={(e)=>setResetKey(e.target.value)} className={`${InputCls} font-mono`} />
              </div>
              <div>
                <Label className="text-[#F5E8D8]/80">New Password (min 8 chars)</Label>
                <Input data-testid="reset-new-password" type="password" required value={newPassword} onChange={(e)=>setNewPassword(e.target.value)} className={InputCls} />
              </div>
              <div>
                <Label className="text-[#F5E8D8]/80">Confirm New Password</Label>
                <Input data-testid="reset-confirm-password" type="password" required value={confirm} onChange={(e)=>setConfirm(e.target.value)} className={InputCls} />
              </div>
            </div>
            <Button type="submit" data-testid="reset-submit" disabled={busy} size="lg" className="w-full mt-6 rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold">
              {busy ? "Resetting..." : "Reset Password"}
            </Button>
          </form>
        )}

        {mode === "done" && (
          <div className="text-center" data-testid="reset-done">
            <div className="w-14 h-14 rounded-full grid place-items-center bg-[#10B981]/15 border border-[#10B981]/40 mx-auto mb-5">
              <CheckCircle2 className="w-7 h-7 text-[#10B981]" />
            </div>
            <h2 className="font-serif text-3xl mb-2">Password updated</h2>
            <p className="text-sm text-[#F5E8D8]/70 mb-8">You can now sign in with your new password.</p>
            <Button data-testid="back-to-login" onClick={() => { setMode("login"); setPassword(""); }} size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-8">
              Back to Sign in
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
